import React from 'react';
import { Link } from 'react-router-dom';
import { Award, BookOpen, CheckCircle, Clock, GraduationCap, Mail, Sparkles, Users } from 'lucide-react';
import { siteData } from '../data/mock';

const AcademyPage = () => {
  const { academy } = siteData;

  return (
    <div className="academy-page" data-testid="academy-page">
      {/* ===== HERO ===== */}
      <section className="ap-hero">
        <span className="ap-hero-glow ap-hero-glow-1" aria-hidden="true" />
        <span className="ap-hero-glow ap-hero-glow-2" aria-hidden="true" />
        <div className="ap-container">
          <div className="ap-hero-grid">
            <div className="ap-hero-copy">
              <div className="ap-crest" aria-hidden="true">
                <span className="ap-crest-laurel ap-crest-laurel-left" />
                <span className="ap-crest-shield">
                  <GraduationCap size={28} />
                </span>
                <span className="ap-crest-laurel ap-crest-laurel-right" />
              </div>

              <p className="ap-institute">{academy.institute}</p>
              <h1 className="ap-title">
                Secondary <span className="ap-title-accent">Leadership</span> Academy
              </h1>
              <p className="ap-subtitle">{academy.certificate}</p>

              <div className="ap-meta-row">
                <div className="ap-meta">
                  <Clock size={16} />
                  <div>
                    <span className="ap-meta-label">Duration</span>
                    <span className="ap-meta-value">{academy.duration}</span>
                  </div>
                </div>
                <div className="ap-meta">
                  <Users size={16} />
                  <div>
                    <span className="ap-meta-label">Format</span>
                    <span className="ap-meta-value">{academy.format}</span>
                  </div>
                </div>
                <div className="ap-meta">
                  <BookOpen size={16} />
                  <div>
                    <span className="ap-meta-label">Instructor</span>
                    <span className="ap-meta-value">{academy.instructor}</span>
                  </div>
                </div>
                <div className="ap-meta">
                  <Award size={16} />
                  <div>
                    <span className="ap-meta-label">Awarded</span>
                    <span className="ap-meta-value">Certificate of Completion</span>
                  </div>
                </div>
              </div>

              <div className="ap-cta-row">
                <span className="ap-badge" data-testid="academy-coming-soon-badge">
                  <Sparkles size={14} /> Coming Soon
                </span>
                <a
                  href="mailto:romero.sumpter@gmail.com?subject=Secondary Leadership Academy — Interest List"
                  className="ap-cta-primary"
                  data-testid="academy-interest-cta"
                >
                  Join the Interest List
                  <Mail size={16} />
                </a>
              </div>
            </div>

            <div className="ap-hero-flyer">
              <img
                src={academy.flyer}
                alt="Secondary Leadership Academy — Coming Soon"
                data-testid="academy-flyer"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== PROGRAM OVERVIEW ===== */}
      <section className="ap-section ap-section-light">
        <div className="ap-container">
          <p className="ap-eyebrow">Program Overview</p>
          <h2 className="ap-section-title">A leadership academy built for the ones <span className="ap-title-accent">behind</span> the leader.</h2>
          <div className="ap-overview-grid">
            {academy.overview.map((para, idx) => (
              <p key={idx} className="ap-overview-para">{para}</p>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LEARNING OUTCOMES ===== */}
      <section className="ap-section">
        <div className="ap-container">
          <p className="ap-eyebrow">Learning Outcomes</p>
          <h2 className="ap-section-title">Upon completion, students will be able to:</h2>
          <div className="ap-outcomes-grid">
            {academy.outcomes.map((outcome, idx) => (
              <div key={idx} className="ap-outcome" data-testid={`academy-outcome-${idx}`}>
                <CheckCircle size={18} className="ap-outcome-check" />
                <span>{outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 12-WEEK CURRICULUM ===== */}
      <section className="ap-section ap-section-dark">
        <div className="ap-container">
          <p className="ap-eyebrow ap-eyebrow-gold">12-Week Curriculum</p>
          <h2 className="ap-section-title ap-section-title-light">
            Twelve weeks. One transformation.
          </h2>
          <div className="ap-curriculum-grid">
            {academy.curriculum.map((week) => (
              <article key={week.week} className="ap-week-card" data-testid={`academy-week-${week.week}`}>
                <div className="ap-week-header">
                  <span className="ap-week-num">Week {String(week.week).padStart(2, '0')}</span>
                  <span className="ap-week-divider" />
                </div>
                <h3 className="ap-week-title">{week.title}</h3>
                <p className="ap-week-subtitle">{week.subtitle}</p>
                <ul className="ap-week-topics">
                  {week.topics.map((topic, idx) => (
                    <li key={idx}>
                      <span className="ap-week-bullet" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHAT YOU'LL TAKE HOME ===== */}
      <section className="ap-section ap-section-light" id="take-home">
        <div className="ap-container">
          <p className="ap-eyebrow">Included With Tuition</p>
          <h2 className="ap-section-title">What you&apos;ll take home.</h2>
          <p className="ap-section-lede">
            Every student receives premium resources designed to equip you for real-world ministry leadership.
          </p>
          <div className="ap-take-home-image-wrap">
            <img
              src={academy.takeHomeImage}
              alt="What You'll Take Home — Student Textbook, Instructor Guide, Executive Resource Manual, Certificate, and Church Administration Templates"
              className="ap-take-home-image"
              data-testid="academy-take-home-image"
            />
          </div>
          <ul className="ap-included-grid">
            {academy.included.map((item, idx) => (
              <li key={idx} className="ap-included-item">
                <CheckCircle size={14} className="ap-included-check" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== WHO SHOULD ENROLL ===== */}
      <section className="ap-section">
        <div className="ap-container">
          <p className="ap-eyebrow">Who Should Enroll?</p>
          <h2 className="ap-section-title">This academy is designed for:</h2>
          <div className="ap-audience-grid">
            {academy.audience.map((role, idx) => (
              <div key={idx} className="ap-audience-pill">{role}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHAT MAKES IT DIFFERENT ===== */}
      <section className="ap-section ap-section-dark">
        <div className="ap-container">
          <p className="ap-eyebrow ap-eyebrow-gold">What Makes This Academy Different?</p>
          <h2 className="ap-section-title ap-section-title-light">
            Beyond leadership theory &mdash; a complete equipping system.
          </h2>
          <p className="ap-section-lede ap-section-lede-light">
            Students graduate with more than knowledge &mdash; they leave with practical systems, templates, and resources they can immediately implement in their churches and organizations.
          </p>
          <div className="ap-differentiators-grid">
            {academy.differentiators.map((item, idx) => (
              <div key={idx} className="ap-diff-card">
                <span className="ap-diff-num">{String(idx + 1).padStart(2, '0')}</span>
                <span className="ap-diff-text">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="ap-final-cta">
        <div className="ap-container">
          <div className="ap-final-card">
            <p className="ap-eyebrow ap-eyebrow-gold">Coming Soon</p>
            <h2 className="ap-section-title ap-section-title-light">
              Be the first to know when enrollment opens.
            </h2>
            <p className="ap-section-lede ap-section-lede-light">
              Join the interest list and receive an invitation the moment registration goes live.
            </p>
            <div className="ap-cta-row ap-cta-row-center">
              <a
                href="mailto:romero.sumpter@gmail.com?subject=Secondary Leadership Academy — Interest List"
                className="ap-cta-primary"
                data-testid="academy-final-cta"
              >
                Join the Interest List
                <Mail size={16} />
              </a>
              <Link to="/books" className="ap-cta-secondary">
                Read &ldquo;Behind the Leader&rdquo;
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AcademyPage;
